/** What a dashcam channel films. The API only numbers channels; which is
 *  which depends on the camera model (see the data layer's TEMPORARY rule):
 *  the story's four positions (S5: 1 Left, 2 Front, 3 Right, 4 Rear), or a
 *  JIMI JC400's two (1 cabin, 2 road). `null`: not known, shown by number. */
export type CameraKind = "left" | "front" | "right" | "rear" | "cabin" | "road";

/** What a camera model's video can do (`protocol.videoFeatures` of
 *  `GET /devices/models`): only these are offered -- the others are `400`.
 *  Today a BSJ dashcam has all five, a JIMI JC400 only LIVE. */
export type VideoFeature = "LIVE" | "RECORDINGS" | "PLAYBACK" | "CLIPS" | "SNAPSHOTS";

export interface VehicleCamera {
  readonly channel: number;
  readonly kind: CameraKind | null;
}

/** A vehicle's dashcam: the device that streams, its cameras, and what its
 *  video can do. No cameras: the device has none, or can't stream live. */
export interface VehicleCameras {
  readonly deviceId: string;
  readonly cameras: readonly VehicleCamera[];
  readonly features: readonly VideoFeature[];
  /** The model streams standard JT/T 1078 (`videoTransport: JT1078`): the
   *  only cameras the operator can talk to (`POST .../video/intercom`). */
  readonly canTalk: boolean;
}

/** The camera to open first: Left when there is one (S5), else the first. */
export function defaultChannel(cameras: readonly VehicleCamera[]): number | null {
  return (cameras.find((camera) => camera.kind === "left") ?? cameras[0])?.channel ?? null;
}

/** A live stream of one channel (`POST /devices/:deviceId/video/live`). The
 *  URLs carry their own token (and, for some models, a longer path): use them
 *  as returned. It ends half a minute after nobody watches; while someone
 *  does, `endsAt` keeps moving later, up to a cap -- so `endsAt` is only the
 *  end as of the response. */
export interface LiveVideoSession {
  readonly sessionId: string;
  readonly channel: number;
  /** HLS playlist: any browser, a few seconds behind. */
  readonly hlsUrl: string | null;
  /** WebRTC (WHEP) endpoint: under a second behind. */
  readonly webrtcUrl: string | null;
  readonly endsAt: string;
}

export type CameraErrorCode =
  /** Not allowed (checked before asking, so this is a backstop). */
  | "NO_ACCESS"
  /** The device or vehicle isn't there (any more). */
  | "NOT_FOUND"
  /** The camera didn't answer its start command. */
  | "CAMERA_UNREACHABLE"
  /** The media server is down. */
  | "MEDIA_UNAVAILABLE"
  /** The channel is busy (e.g. a clip is being saved from it). */
  | "BUSY"
  /** The device isn't connected to the tracking service right now. */
  | "OFFLINE"
  | "FAILED";

export class CameraRequestError extends Error {
  readonly code: CameraErrorCode;

  constructor(code: CameraErrorCode, message = "") {
    super(message || code);
    this.name = "CameraRequestError";
    this.code = code;
  }
}

export function isCameraRequestError(value: unknown): value is CameraRequestError {
  return value instanceof CameraRequestError;
}

/** A talk to a dashcam's speaker (`POST /devices/:deviceId/video/intercom`). */
export interface IntercomSession {
  readonly sessionId: string;
  readonly channel: number;
  /** The WebSocket the operator's voice goes to. It carries its own key: a
   *  credential, never logged or kept. */
  readonly talkUrl: string;
  readonly endsAt: string;
}

export type TalkErrorCode =
  /** No microphone, or the browser was refused access to it. */
  | "MIC_DENIED"
  /** Not a JT/T 1078 camera, no such channel, or no speaker. */
  | "NOT_SUPPORTED"
  /** Someone else is talking to this camera. */
  | "BUSY"
  /** The device isn't connected right now. */
  | "OFFLINE"
  /** The camera refused, didn't answer, or didn't dial in in time. */
  | "CAMERA_UNREACHABLE"
  | "NO_ACCESS"
  /** Talk isn't set up on this server. */
  | "UNAVAILABLE"
  | "FAILED";

export class TalkError extends Error {
  readonly code: TalkErrorCode;

  constructor(code: TalkErrorCode, message = "") {
    super(message || code);
    this.name = "TalkError";
    this.code = code;
  }
}

/** A stretch of footage a camera holds on its own storage
 *  (`GET /devices/:deviceId/video/recordings`). */
export interface CameraRecording {
  readonly channel: number;
  readonly startAt: string;
  readonly endAt: string;
}

/** A period of recorded video, ISO 8601. */
export interface RecordedVideoRange {
  readonly from: string;
  readonly to: string;
}

/** The speeds a camera plays its recordings back at (`PATCH` `speed`). */
export const CAMERA_PLAYBACK_SPEEDS = [1, 2, 4, 8, 16] as const;
export type CameraPlaybackSpeed = (typeof CAMERA_PLAYBACK_SPEEDS)[number];

/** What a playing recording can be told
 *  (`PATCH /devices/:deviceId/video/playback/:sessionId`). */
export type RecordedVideoControl =
  | { readonly action: "pause" }
  | { readonly action: "resume" }
  | { readonly action: "fastForward"; readonly speed: CameraPlaybackSpeed }
  | { readonly action: "seek"; readonly position: string };

/**
 * One archived file of a channel (`GET /tracking/vehicles/:assetId/video/
 * archive`): an MP4 the browser plays itself -- speed, pause and seek need no
 * request. Laid on the map playback's UTC clock: the moment T is
 * `T - startMs` into the file. Only the video around a HIGH or VERY_HIGH
 * alert is archived (a minute before to a minute after).
 */
export interface ArchiveSegment {
  readonly id: string;
  /** The device that recorded it: a vehicle may have had several. */
  readonly deviceId: string;
  readonly channel: number;
  readonly startMs: number;
  readonly endMs: number;
  /** Private; stops working at `urlExpiresMs` (15 minutes after asking). */
  readonly url: string;
  readonly urlExpiresMs: number;
}

/** A channel's archive over a window of at most a day, oldest first. */
export interface ArchiveTimeline {
  readonly channel: number;
  readonly fromMs: number;
  readonly toMs: number;
  readonly segments: readonly ArchiveSegment[];
}
